import { Box, Checkbox, FormControlLabel, Typography } from '@mui/material';
import { ColDef } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { isBefore, parseISO } from 'date-fns';
import { chain, maxBy } from 'lodash';
import { useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { ExchangeRateDto } from '../../common/PricePrgClient';
import { RootState } from '../../store';
import { toDateString } from '../../utils';
import { getAppSettings } from '../settings/redux/settingsSelectors';
import { getExchangeRates } from './redux/exchangeRateSelectors';
import { getExchangeRateKey } from './redux/exchangeRateSlices';

const mapStateToProps = (state: RootState) => ({
    exchangeRates: getExchangeRates(state),
    appSettings: getAppSettings(state)
});

type Props = ReturnType<typeof mapStateToProps>;

const columns: ColDef[] = [
    {
        field: 'currency',
        headerName: 'Currency',
        width: 100
    },
    {
        field: 'validFrom',
        headerName: 'Valid from',
        width: 120,
        valueFormatter: params => toDateString(params.value)
    },
    {
        field: 'rate',
        headerName: 'Rate',
        width: 100
    }
];

const defaultColDefs = {
    filter: true,
    sortable: true,
    resizable: true,
    editable: false,
    floatingFilter: true,
    floatingFilterComponentParams: {
        suppressFilterButton: true
    }
};

const ExchangeRates = ({ exchangeRates, appSettings }: Props) => {
    const [showOnlyActiveExchangeRates, setShowOnlyActiveExchangeRates] = useState(true);

    const toggleShowOnlyActiveExchangeRates = () => {
        setShowOnlyActiveExchangeRates(x => !x);
    };

    const exchangeRatesList = useMemo(() => {
        let rates = Object.values(exchangeRates);

        if (appSettings.defaultCurrency) {
            // Don't show default currency
            rates = rates.filter(x => x.currency != appSettings.defaultCurrency);
        }

        if (appSettings.inverseExchangeRates) {
            rates = rates.map(r => ({ ...r, rate: +(1 / r.rate).toFixed(4) }));
        }

        if (showOnlyActiveExchangeRates) {
            return chain(rates)
                .filter(x => isBefore(parseISO(x.validFrom), new Date()))
                .groupBy('currency')
                .map(ratesByCurrency => maxBy(ratesByCurrency, rate => parseISO(rate.validFrom))!)
                .value();
        }

        return rates;
    }, [exchangeRates, showOnlyActiveExchangeRates, appSettings]);

    if (Object.keys(exchangeRates).length === 0) {
        return null;
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="h5">Exchange rates</Typography>
            <div>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={showOnlyActiveExchangeRates}
                            onChange={toggleShowOnlyActiveExchangeRates}
                            color="primary"
                            size="small"
                        />
                    }
                    label="Show only active exchange rates"
                />
                <AgGridReact<ExchangeRateDto>
                    className="ag-theme-alpine"
                    columnDefs={columns}
                    defaultColDef={defaultColDefs}
                    rowData={exchangeRatesList}
                    headerHeight={50}
                    floatingFiltersHeight={30}
                    getRowId={params => getExchangeRateKey(params.data)}
                    containerStyle={{
                        height: '300px',
                        width: '400px',
                        overflowY: 'auto'
                    }}
                />
            </div>
        </Box>
    );
};
export default connect(mapStateToProps)(ExchangeRates);
